import { useCallback, useEffect } from 'react'

import { MessageSort } from '@api'
import { useUnit } from 'effector-react'
import { useNavigate } from 'react-router'

import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { SortOptionsSelect } from '@/components/ui/table/components/sort-options-select.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { $creatorId, $isCreatorProcessCompleted } from '@/models/creators.ts'
import { SettingsPageTitle } from '@/modules/creator-settings/SettingsPageTitle'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption.tsx'
import { ExpiringFansPageSkeleton } from '@/modules/expiring-fans/components/expiring-fans-page-skeleton'
import { MessageTimingController } from '@/modules/expiring-fans/components/partials/message-timing-controller.tsx'
import { SpendLimitationController } from '@/modules/expiring-fans/components/partials/spend-limitation-controller.tsx'
import { TimeLimitationController } from '@/modules/expiring-fans/components/partials/time-limitation-controller.tsx'
import { ExpiringFansTableSkeleton } from '@/modules/expiring-fans/components/tables/fans/expiring-fans-table-skeleton.tsx'
import { ExpiringFansMessagesTable } from '@/modules/expiring-fans/components/tables/messages/expiring-fans-messages-table.tsx'
import {
  $expiringFansMessageSortOptions,
  setSortOption,
} from '@/modules/expiring-fans/models.ts'
import { useExpiringFansQuery } from '@/modules/expiring-fans/query/expiring-fans-query-context'
import type { ExpiringFan } from '@/modules/expiring-fans/service/expiring-fans-service'
import { useExpiringFanSettings } from '@/modules/expiring-fans/use-expiring-fans-settings'
import { handleError } from '@/utils/error.ts'
import {
  TrackedFeatures,
  trackFeatureUsage,
} from '@/utils/feature-engagement.ts'
import { usePagination } from '@/utils/pagination/pagination-context.tsx'

import { ExpiringFansTable } from './tables/fans/expiring-fans-table.tsx'

const sortOptions = {
  Default: MessageSort.DEFAULT,
  Sent_To: MessageSort.SENT_TO,
  Reads: MessageSort.READS,
  Responses: MessageSort.RESPONSES,
  PPVs_Bought: MessageSort.PPV_BOUGHT,
}

export const ExpiringFansPage = () => {
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)
  const sortOption = useUnit($expiringFansMessageSortOptions)
  const creatorId = useUnit($creatorId)

  const redirect = useNavigate()

  const data = useExpiringFansQuery()
  const {
    pagination,
    setPagination,
    setSearchQuery,
    searchQuery,
    sorting,
    setSorting,
  } = usePagination()

  const {
    messages,

    deleteMessage,
    markAsMessaged,
    markAsNotMessaged,
    isUpdating,
    enabled,
    changeEnabled,
    hoursBeforeExpiry,
    changeHoursBeforeExpiry,
    maxSpend,
    changeMaxSpend,
    minHoursSinceLastMessage,
    changeMinHoursSinceLastMessage,

    handleToggleChange,
  } = useExpiringFanSettings()

  const onToggleSettings = useCallback(
    () => changeEnabled(!enabled),
    [changeEnabled, enabled],
  )

  const toast = useToast()

  const handleMarkAsMessaged = useCallback(
    (fan: ExpiringFan) => {
      markAsMessaged(fan.fan.id)
        .then(() => {
          toast.success(`Successfully marked as messaged`)
        })
        .catch((error) => {
          handleError(error, `Failed to mark as messaged`)
          console.warn('Failed to mark as messaged', error)
        })
    },
    [toast, markAsMessaged],
  )

  const handleMarkAsNotMessaged = useCallback(
    (fan: ExpiringFan) => {
      markAsNotMessaged(fan.fan.id)
        .then(() => {
          toast.success(`Successfully marked as not messaged`)
        })
        .catch((error) => {
          handleError(error, `Failed to mark as not messaged`)
          console.warn('Failed to mark as not messaged', error)
        })
    },
    [toast, markAsNotMessaged],
  )

  const handleRedirectToCreateMessage = () => {
    redirect('create')
  }

  useEffect(() => {
    if (enabled && data?.content?.length) {
      trackFeatureUsage(TrackedFeatures.ExpiringFans, creatorId)
    }
  }, [data, enabled, creatorId])

  if (!isCreatorProcessCompleted) {
    return <ExpiringFansPageSkeleton />
  }

  return (
    <Page>
      <div className="flex flex-col gap-[32px]">
        <SettingsPageTitle
          title="Expiring Fans Messages"
          description={
            <span className="flex items-center gap-2">
              Automatically sends messages to fans whose subscriptions are
              expiring soon
            </span>
          }
          toggleName="toggleExpiring"
          enabled={enabled}
          onChange={onToggleSettings}
          disabled={isUpdating}
        />
        <Separator />
        <div className="flex flex-col gap-[32px]">
          <MessageTimingController
            hoursBeforeExpiry={hoursBeforeExpiry}
            changeHoursBeforeExpiry={changeHoursBeforeExpiry}
            disabled={isUpdating}
          />
          <Separator />
          <SpendLimitationController
            maxSpend={maxSpend}
            changeMaxSpend={changeMaxSpend}
            disabled={isUpdating}
          />
          <Separator />
          <TimeLimitationController
            minHoursSinceLastMessage={minHoursSinceLastMessage}
            changeMinHoursSinceLastMessage={changeMinHoursSinceLastMessage}
            disabled={isUpdating}
          />
          <Separator />

          <SettingsOption
            title="Messages"
            description="Configure multiple messages that will be sent to your fans"
            mobileVertical
            biggerTitle
          >
            <div className="flex gap-2">
              <SortOptionsSelect
                sortOptions={sortOptions}
                sortOption={sortOption}
                handleSortChange={setSortOption}
              />
              <div>
                <Button onClick={handleRedirectToCreateMessage}>
                  <PlusIcon /> Create Message
                </Button>
              </div>
            </div>
          </SettingsOption>
          <ExpiringFansMessagesTable
            handleToggleChange={handleToggleChange}
            deleteMessage={deleteMessage}
            messages={messages}
          />
        </div>
        {data?._isLoading ? (
          <ExpiringFansTableSkeleton />
        ) : (
          <ExpiringFansTable
            entries={data?.content ?? []}
            onMarkAsMessaged={handleMarkAsMessaged}
            onMarkAsNotMessaged={handleMarkAsNotMessaged}
            paginationState={pagination}
            onPaginationChange={setPagination}
            pageCount={data.totalPages ?? 1}
            setSearchQuery={setSearchQuery}
            searchQuery={searchQuery}
            sorting={sorting}
            setSorting={setSorting}
          />
        )}
      </div>
    </Page>
  )
}
